import { Api, ApiError } from './api'
import type { CreateSubmissionBody } from './api'
import { DEFAULT_LGPD_TEXT, DEFAULT_MESSAGES, UI_TEXT, deepMerge, defaultSettings } from './defaults'
import { h, joinClasses } from './dom'
import { maskPhone, normalizePhone, onlyDigits } from './mask'
import { BRAZIL_STATES } from './states'
import {
  SHAPE_KEY_PREFIX,
  SUBMISSION_KEY,
  TOKEN_PARAM,
  readJson,
  readSubmission,
  removeKey,
  saveSubmission,
  writeJson,
} from './storage'
import { buildCss, injectFont } from './styles'
import {
  collectTracking,
  conversionsEnabled,
  facebookCookies,
  newEventId,
  pushDataLayer,
  queryParam,
  trackPixel,
} from './tracking'
import type {
  ButtonState,
  ClassKey,
  DeepPartial,
  FieldDefinition,
  FieldOption,
  FormDefinition,
  FormError,
  FormEventHandler,
  FormEventMap,
  FormEventName,
  FormOptions,
  PublicSettings,
  ValidationMessages,
} from './types'
import { containsBlockedWord, validateField } from './validate'

type Control = HTMLInputElement | HTMLSelectElement

type FieldState = {
  def: FieldDefinition
  control: Control
  error: HTMLElement | null
}

type AnyHandler = (payload: never) => void

const BUTTON_CLASS: Record<ButtonState, ClassKey | null> = {
  idle: null,
  sending: 'buttonSending',
  redirecting: 'buttonRedirecting',
  sent: 'buttonSent',
}

const PREVIEW_DELAY_MS = 600
const FAKE_DELAY_MS = 400
const DEFAULT_SKELETON_ROWS = 3

/**
 * O que a última visita desenhou: quantos campos, se tinha o aceite de LGPD e a altura real, por
 * tamanho de tela. Com isso o skeleton ocupa o mesmo espaço e a troca pelo formulário não empurra a
 * página. Versões antigas guardavam só o número de campos.
 */
type Shape = { rows: number; lgpd: boolean; alturas: Partial<Record<'celular' | 'computador', number>> }

function readShape(publicId: string): Shape | null {
  const stored = readJson<Shape | number>(SHAPE_KEY_PREFIX + publicId)
  if (typeof stored === 'number') return { rows: stored, lgpd: false, alturas: {} }
  if (stored && typeof stored.rows === 'number') return { rows: stored.rows, lgpd: !!stored.lgpd, alturas: stored.alturas ?? {} }
  return null
}

const tela = (): 'celular' | 'computador' => (window.innerWidth < 640 ? 'celular' : 'computador')

const querMenosMovimento = (): boolean =>
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches

let instanceCounter = 0
const citiesCache = new Map<string, Promise<string[]>>()
/** Um PageView por id de formulário, por carregamento de página. */
const pageviewsSent = new Set<string>()

function optionPair(option: FieldOption): { value: string; label: string } {
  return typeof option === 'string' ? { value: option, label: option } : { value: String(option.value), label: String(option.label) }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, Math.max(0, ms)))
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

export function isSafeNavigationUrl(url: string): boolean {
  try {
    const protocol = new URL(url, window.location.href).protocol
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

function stripTokenFromUrl(): void {
  try {
    const current = new URL(window.location.href)
    if (!current.searchParams.has(TOKEN_PARAM)) return
    current.searchParams.delete(TOKEN_PARAM)
    window.history.replaceState(window.history.state, '', current.toString())
  } catch {
    // sem history (iframe sandbox): o token só fica na barra
  }
}

export function appendTokenIfCrossHost(url: string, token: string | null): string {
  if (!token) return url
  try {
    const target = new URL(url, window.location.href)
    if (target.host === window.location.host) return target.toString()
    target.searchParams.set(TOKEN_PARAM, token)
    return target.toString()
  } catch {
    return url
  }
}

export class Form {
  private readonly options: FormOptions
  private readonly publicId: string
  private readonly api: Api | null
  private readonly preview: boolean
  private readonly uid: string
  private readonly listeners = new Map<FormEventName, Set<AnyHandler>>()
  private readonly cleanups: (() => void)[] = []
  private readonly timers = new Set<ReturnType<typeof setTimeout>>()
  private readonly fields = new Map<string, FieldState>()

  private host: HTMLElement | null = null
  private root: HTMLElement | null = null
  private themeStyle: HTMLStyleElement | null = null
  private formEl: HTMLFormElement | null = null
  private pageForm: HTMLFormElement | null = null
  private button: HTMLButtonElement | null = null
  private formError: HTMLElement | null = null
  private successEl: HTMLElement | null = null
  private lgpdInput: HTMLInputElement | null = null
  private lgpdError: HTMLElement | null = null
  private honeypot: HTMLInputElement | null = null
  private dialog: HTMLDialogElement | null = null

  private definition: FormDefinition | null = null
  private settings: PublicSettings
  private messages: ValidationMessages
  private pendingValues: Record<string, string>
  private token: string | null = null
  private buttonState: ButtonState = 'idle'
  private renderedAt = 0
  private started = false
  private attempted = false
  private destroyed = false
  private cityRequest = 0

  constructor(options: FormOptions) {
    if (!options || typeof options !== 'object') throw new TypeError('[form] Opções inválidas.')
    const publicId = options.form || options.definition?.id
    if (!publicId) throw new TypeError('[form] Informe "form" com o id público do formulário.')
    this.preview = options.preview === true
    if (!this.preview && !options.apiBase) {
      throw new TypeError('[form] Informe "apiBase" com o endereço do servidor dos formulários.')
    }
    this.options = options
    this.publicId = publicId
    this.api = this.preview ? null : new Api(options.apiBase as string)
    this.uid = `lf${++instanceCounter}`
    this.settings = this.resolveSettings(options.definition?.type ?? 'create', undefined)
    this.messages = { ...DEFAULT_MESSAGES, ...options.messages }
    this.pendingValues = { ...options.values }
    this.start()
  }

  on<E extends FormEventName>(event: E, handler: FormEventHandler<E>): () => void {
    const set = this.listeners.get(event) ?? new Set<AnyHandler>()
    set.add(handler as AnyHandler)
    this.listeners.set(event, set)
    return () => this.off(event, handler)
  }

  off<E extends FormEventName>(event: E, handler: FormEventHandler<E>): void {
    this.listeners.get(event)?.delete(handler as AnyHandler)
  }

  submit(): Promise<void> {
    return this.handleSubmit()
  }

  reset(): void {
    this.clearTimers()
    this.attempted = false
    if (!this.formEl) {
      this.pendingValues = { ...this.options.values }
      return
    }
    this.formEl.reset()
    this.formEl.hidden = false
    this.successEl?.remove()
    this.successEl = null
    this.fields.forEach((state) => this.setFieldError(state, null))
    this.setLgpdError(null)
    this.setFormError('')
    this.setButton('idle')
    this.applyValues({ ...this.initialValues(), ...this.options.values })
  }

  setValues(values: Record<string, string>): void {
    if (!this.formEl) {
      this.pendingValues = { ...this.pendingValues, ...values }
      return
    }
    this.applyValues(values)
  }

  destroy(): void {
    if (this.destroyed) return
    this.destroyed = true
    this.api?.abortAll()
    this.clearTimers()
    this.cleanups.splice(0).forEach((cleanup) => cleanup())
    this.host?.remove()
    this.host = null
    this.listeners.clear()
    this.fields.clear()
  }

  private emit<E extends FormEventName>(event: E, payload: FormEventMap[E]): number {
    const handlers = this.listeners.get(event)
    if (!handlers || handlers.size === 0) return 0
    handlers.forEach((handler) => {
      try {
        ;(handler as FormEventHandler<E>)(payload)
      } catch (error) {
        console.error(`[form] Erro no listener de "${event}":`, error)
      }
    })
    return handlers.size
  }

  private later(callback: () => void, ms: number): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer)
      if (!this.destroyed) callback()
    }, ms)
    this.timers.add(timer)
  }

  private clearTimers(): void {
    this.timers.forEach((timer) => clearTimeout(timer))
    this.timers.clear()
  }

  private listen(target: EventTarget, type: string, handler: (event: Event) => void, passive = true): void {
    target.addEventListener(type, handler, { passive })
    this.cleanups.push(() => target.removeEventListener(type, handler))
  }

  private cls(base: string, key: ClassKey): string {
    return joinClasses(base, this.options.classes?.[key])
  }

  private resolveSettings(type: FormDefinition['type'], fromServer: DeepPartial<PublicSettings> | undefined): PublicSettings {
    const { pipeId: _pipeId, stageId: _stageId, ...overrides } = this.options.overrides ?? {}
    return deepMerge(deepMerge(defaultSettings(type), fromServer), overrides as DeepPartial<PublicSettings>)
  }

  private start(): void {
    const target = this.options.target
    if (typeof target !== 'string') {
      this.mount(target)
      return
    }
    const found = document.querySelector<HTMLElement>(target)
    if (found) {
      this.mount(found)
      return
    }
    if (document.readyState === 'loading') {
      this.listen(document, 'DOMContentLoaded', () => {
        const element = document.querySelector<HTMLElement>(target)
        if (element) this.mount(element)
        else this.failTarget(target)
      })
      return
    }
    this.failTarget(target)
  }

  private failTarget(target: string): void {
    console.error(`[form] Elemento "${target}" não encontrado na página.`)
    queueMicrotask(() => this.emit('error', { code: 'target_not_found', message: `Elemento "${target}" não encontrado.` }))
  }

  private mount(target: HTMLElement): void {
    if (this.destroyed) return
    const host = h('div', { class: 'lf-host' })
    const { minHeight } = this.options
    if (minHeight !== undefined) host.style.minHeight = typeof minHeight === 'number' ? `${minHeight}px` : minHeight
    const useShadow = this.options.shadow !== false && typeof host.attachShadow === 'function'
    const container: ShadowRoot | HTMLElement = useShadow ? host.attachShadow({ mode: 'open' }) : host

    if (useShadow && this.options.inheritPageStyles) {
      document.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => container.appendChild(node.cloneNode(true)))
    }
    this.themeStyle = h('style')
    container.appendChild(this.themeStyle)
    if (this.options.css) container.appendChild(h('style', {}, [this.options.css]))

    this.root = h('div', { class: this.cls('lf-root', 'root'), 'data-lf-state': 'loading' })
    container.appendChild(this.root)
    if (useShadow) {
      this.pageForm = h('form', { class: 'lf-page-form', name: this.publicId, method: 'post', hidden: true, 'aria-hidden': 'true' })
      host.appendChild(this.pageForm)
    }
    this.host = host
    this.updateStyle(useShadow)
    target.appendChild(host)

    if (this.options.definition) {
      this.applyDefinition(this.options.definition)
      return
    }
    this.renderSkeleton()
    this.fetchDefinition()
  }

  private updateStyle(shadow = this.host?.shadowRoot != null): void {
    if (!this.themeStyle) return
    this.themeStyle.textContent = (shadow ? ':host{display:block}' : '') + buildCss(this.settings.theme, '.lf-root')
  }

  private renderSkeleton(): void {
    if (!this.root) return
    const shape = readShape(this.publicId)
    const skeleton = h('div', { class: 'lf-skeleton', 'aria-busy': 'true', 'aria-label': UI_TEXT.loading, role: 'status' })
    const reservada = shape?.alturas[tela()] ?? this.alturaReservada()
    const estilo = getComputedStyle(this.root)
    const respiro = (parseFloat(estilo.paddingTop) || 0) + (parseFloat(estilo.paddingBottom) || 0)
    // Primeira visita: sem saber quantos campos há, estima pelos que cabem na altura reservada (um
    // campo com rótulo ocupa ~5,3 vezes a letra; o botão, ~2,8) — três linhas soltas num espaço
    // grande parecem formulário quebrado.
    const letra = parseFloat(estilo.fontSize) || 16
    const estimadas = reservada ? Math.floor((reservada - respiro - letra * 2.8) / (letra * 5.3)) : 0
    const rows = shape && shape.rows > 0 ? Math.min(shape.rows, 12) : Math.min(Math.max(estimadas, DEFAULT_SKELETON_ROWS), 12)
    if (reservada && reservada > respiro) skeleton.style.minHeight = `${reservada - respiro}px`
    for (let index = 0; index < rows; index++) {
      skeleton.append(
        h('div', { class: 'lf-skeleton-row' }, [
          h('div', { class: 'lf-skeleton-bar lf-skeleton-label' }),
          h('div', { class: 'lf-skeleton-bar lf-skeleton-input' }),
        ])
      )
    }
    if (shape?.lgpd) skeleton.append(h('div', { class: 'lf-skeleton-bar lf-skeleton-lgpd' }))
    skeleton.append(h('div', { class: 'lf-skeleton-bar lf-skeleton-button' }))
    this.root.replaceChildren(skeleton)
  }

  private alturaReservada(): number | null {
    const { minHeight } = this.options
    if (typeof minHeight === 'number') return minHeight
    const pixels = typeof minHeight === 'string' && /^\d+(\.\d+)?px$/.test(minHeight.trim()) ? parseFloat(minHeight) : NaN
    return Number.isNaN(pixels) ? null : pixels
  }

  /** A altura sai da do skeleton e vai até a do formulário, sem pulo. */
  private suavizarTroca(root: HTMLElement, antes: number): void {
    const depois = root.getBoundingClientRect().height
    if (!antes || !depois || Math.abs(depois - antes) < 2 || querMenosMovimento()) return
    root.style.height = `${antes}px`
    root.style.overflow = 'hidden'
    const soltar = () => {
      root.style.height = ''
      root.style.overflow = ''
      root.style.transition = ''
    }
    requestAnimationFrame(() => {
      root.style.transition = 'height .3s cubic-bezier(.2,.7,.2,1)'
      root.style.height = `${depois}px`
    })
    root.addEventListener('transitionend', soltar, { once: true })
    setTimeout(soltar, 450)
  }

  private guardarForma(definition: FormDefinition, root: HTMLElement): void {
    if (this.preview) return
    const anterior = readShape(this.publicId)
    const altura = Math.round(root.getBoundingClientRect().height)
    writeJson(SHAPE_KEY_PREFIX + this.publicId, {
      rows: definition.fields.filter((field) => !field.hidden).length,
      lgpd: this.settings.lgpd.enabled,
      alturas: { ...anterior?.alturas, ...(altura > 0 ? { [tela()]: altura } : {}) },
    } satisfies Shape)
  }

  private fetchDefinition(): void {
    if (!this.api) return
    this.api
      .getDefinition(this.publicId)
      .then((definition) => {
        if (!this.destroyed) this.applyDefinition(definition)
      })
      .catch((error: unknown) => {
        if (this.destroyed) return
        const detail: FormError =
          error instanceof ApiError
            ? { code: error.detail.code, message: error.detail.message }
            : { code: 'unknown', message: UI_TEXT.loadError }
        this.showLoadError(detail)
      })
  }

  /**
   * Uma visita é uma visita: se a mesma página desenhar o formulário duas vezes (duas chamadas do
   * SDK, ou uma troca de rota que remonta), o segundo PageView seria uma visita inventada.
   */
  private trackPageview(): void {
    if (this.preview || !this.api) return
    if (!this.settings.tracking.pageView) return
    if (!conversionsEnabled()) return
    if (pageviewsSent.has(this.publicId)) return

    pageviewsSent.add(this.publicId)
    const eventId = newEventId()
    trackPixel('PageView', eventId)
    this.api.sendPageview(this.publicId, {
      eventId,
      pageUrl: window.location.href,
      referrer: document.referrer || undefined,
      ...facebookCookies(),
    })
  }

  private showLoadError(detail: FormError): void {
    if (!this.root) return
    this.root.setAttribute('data-lf-state', 'error')
    const listeners = this.emit('error', detail)
    this.root.replaceChildren(listeners > 0 ? '' : h('p', { class: 'lf-message', role: 'alert', part: 'error' }, [UI_TEXT.loadError]))
  }

  private applyDefinition(definition: FormDefinition): void {
    if (!definition || !Array.isArray(definition.fields)) {
      this.showLoadError({ code: 'form_unavailable', message: UI_TEXT.loadError })
      return
    }
    this.definition = definition
    this.settings = this.resolveSettings(definition.type, definition.settings)
    injectFont(this.settings.theme.font)
    this.updateStyle()
    this.trackPageview()
    const missingToken = definition.type === 'update' && !this.resolveToken()
    this.renderForm(definition)
    queueMicrotask(() => {
      if (this.destroyed) return
      this.emit('ready', { definition })
      if (missingToken) this.handleMissingSubmission()
    })
  }

  private resolveToken(): boolean {
    if (this.preview) {
      this.token = 'preview'
      return true
    }
    const fromUrl = queryParam(TOKEN_PARAM)
    const stored = readSubmission()
    if (fromUrl && stored?.token !== fromUrl) saveSubmission(fromUrl, {})
    if (fromUrl) stripTokenFromUrl()
    this.token = fromUrl || stored?.token || null
    return this.token !== null
  }

  private initialValues(): Record<string, string> {
    if (this.definition?.type !== 'update' || this.preview) return {}
    const stored = readSubmission()
    return stored && stored.token === this.token ? { ...stored.values } : {}
  }

  private handleMissingSubmission(): void {
    const url = this.settings.afterSubmit.missingSubmissionRedirectUrl
    const listeners = this.emit('error', { code: 'missing_submission', message: UI_TEXT.missingSubmission })
    if (url) {
      this.redirect(url, null)
      return
    }
    if (listeners === 0) this.setFormError(UI_TEXT.missingSubmission)
  }

  private renderForm(definition: FormDefinition): void {
    const root = this.root
    if (!root) return
    this.fields.clear()
    const form = h('form', { class: 'lf-form lf-entra', novalidate: true, 'data-lf-type': definition.type })
    const hasState = definition.fields.some((field) => field.kind === 'state')

    for (const field of definition.fields) {
      if (field.hidden) {
        const control = h('input', { type: 'hidden', name: field.key })
        this.fields.set(field.key, { def: field, control, error: null })
        form.append(control)
        continue
      }
      form.append(this.renderField(field, hasState))
    }

    if (this.settings.lgpd.enabled) form.append(this.renderLgpd())
    if (this.settings.protection.honeypot) {
      this.honeypot = h('input', { type: 'text', name: 'lf_hp', tabindex: -1, autocomplete: 'off' })
      form.append(h('div', { class: 'lf-hp', 'aria-hidden': 'true' }, [h('label', {}, ['Deixe em branco', this.honeypot])]))
    }

    this.button = h('button', { type: 'submit', class: this.cls('lf-button', 'button'), part: 'button' })
    this.formError = h('p', { class: 'lf-form-error', part: 'error', role: 'alert' })
    form.append(h('div', { class: 'lf-actions' }, [this.button, this.formError]))

    this.formEl = form
    const antes = root.getBoundingClientRect().height
    root.replaceChildren(form)
    root.setAttribute('data-lf-state', 'ready')
    this.suavizarTroca(root, antes)
    this.guardarForma(definition, root)
    setTimeout(() => {
      if (!this.destroyed && this.root === root && root.getAttribute('data-lf-state') === 'ready') this.guardarForma(definition, root)
    }, 600)
    this.setButton('idle')
    this.bindForm(form)
    this.renderedAt = now()
    this.applyValues({ ...this.initialValues(), ...this.pendingValues })
    this.pendingValues = {}
  }

  private renderField(field: FieldDefinition, hasState: boolean): HTMLElement {
    const id = `${this.uid}-${field.key}`
    const errorId = `${id}-error`
    const columns = Math.min(12, Math.max(1, Math.round(field.columns ?? 12) || 12))
    const control = this.renderControl(field, id, hasState)
    control.classList.add(...joinClasses('lf-input', this.options.classes?.input).split(' '))
    control.setAttribute('part', 'input')
    if (field.required) control.setAttribute('aria-required', 'true')

    const error = h('p', { id: errorId, class: this.cls('lf-error', 'error'), part: 'error', 'aria-live': 'polite' })
    this.fields.set(field.key, { def: field, control, error })

    const label = h('label', { for: id, class: this.cls('lf-label', 'label'), part: 'label' }, [
      field.label,
      field.required ? h('span', { class: 'lf-req', 'aria-hidden': 'true' }, ['*']) : null,
    ])
    const wrapper = h('div', { class: this.cls('lf-field', 'field'), part: 'field', 'data-lf-field': field.key }, [label, control, error])
    wrapper.style.gridColumn = `span ${columns}`
    return wrapper
  }

  private renderControl(field: FieldDefinition, id: string, hasState: boolean): Control {
    const placeholder = field.placeholder || null
    const base = { id, name: field.key }

    if (field.kind === 'selectbox' || field.kind === 'state') {
      const options = field.options?.length ? field.options.map(optionPair) : field.kind === 'state' ? BRAZIL_STATES : []
      const empty = placeholder || (field.kind === 'state' ? UI_TEXT.statePlaceholder : UI_TEXT.selectPlaceholder)
      return h('select', { ...base, autocomplete: field.kind === 'state' ? 'address-level1' : null }, [
        h('option', { value: '' }, [empty]),
        ...options.map((option) => h('option', { value: option.value }, [option.label])),
      ])
    }

    if (field.kind === 'city' && hasState) {
      return h('select', { ...base, disabled: true, autocomplete: 'address-level2' }, [h('option', { value: '' }, [UI_TEXT.cityWaiting])])
    }

    if (field.kind === 'datetime') return h('input', { ...base, type: 'datetime-local' })

    if (field.kind === 'phone') {
      return h('input', { ...base, type: 'tel', inputmode: 'tel', autocomplete: 'tel', placeholder: placeholder ?? '(00) 00000-0000', maxlength: 20 })
    }

    const inputType = field.inputType
    const numeric = field.kind === 'number' || inputType === 'number' || inputType === 'integer'
    const autocomplete = inputType === 'email' ? 'email' : field.key === 'title' ? 'name' : field.kind === 'city' ? 'address-level2' : null
    return h('input', {
      ...base,
      type: inputType === 'email' ? 'email' : 'text',
      inputmode: numeric ? (inputType === 'integer' ? 'numeric' : 'decimal') : null,
      autocomplete,
      placeholder,
      maxlength: typeof field.maxLength === 'number' && field.maxLength > 0 ? field.maxLength : null,
    })
  }

  private renderLgpd(): HTMLElement {
    const lgpd = this.settings.lgpd
    const id = `${this.uid}-lgpd`
    this.lgpdInput = h('input', { type: 'checkbox', id, name: 'lgpd', 'aria-required': lgpd.required ? 'true' : null })
    this.lgpdError = h('p', { id: `${id}-error`, class: this.cls('lf-error', 'error'), part: 'error', 'aria-live': 'polite' })
    const opener =
      lgpd.source === 'url' && lgpd.url && isSafeNavigationUrl(lgpd.url)
        ? h('a', { class: 'lf-link', href: lgpd.url, target: '_blank', rel: 'noopener noreferrer' }, [UI_TEXT.lgpdOpen])
        : h('button', { type: 'button', class: 'lf-link', 'aria-haspopup': 'dialog' }, [UI_TEXT.lgpdOpen])
    if (opener instanceof HTMLButtonElement) this.listen(opener, 'click', () => this.openLgpdDialog())
    this.listen(this.lgpdInput, 'change', () => {
      if (this.lgpdInput?.checked) this.setLgpdError(null)
    })
    return h('div', { class: this.cls('lf-lgpd', 'lgpd'), part: 'lgpd' }, [
      h('div', { class: 'lf-lgpd-row' }, [
        this.lgpdInput,
        h('label', { for: id }, [lgpd.label, lgpd.required ? h('span', { class: 'lf-req', 'aria-hidden': 'true' }, ['*']) : null]),
        opener,
      ]),
      this.lgpdError,
    ])
  }

  private openLgpdDialog(): void {
    if (!this.root) return
    if (!this.dialog) {
      const lgpd = this.settings.lgpd
      const text = (lgpd.source === 'custom' && lgpd.customText) || DEFAULT_LGPD_TEXT
      const titleId = `${this.uid}-lgpd-title`
      const close = h('button', { type: 'button', class: 'lf-button lf-secondary' }, [UI_TEXT.lgpdClose])
      const accept = h('button', { type: 'button', class: 'lf-button' }, [UI_TEXT.lgpdAccept])
      const dialog = h('dialog', { class: 'lf-dialog', 'aria-labelledby': titleId, part: 'dialog' }, [
        h('div', { class: 'lf-dialog-body' }, [
          h('h2', { id: titleId }, [UI_TEXT.lgpdTitle]),
          ...text
            .split(/\n{2,}/)
            .filter((paragraph) => paragraph.trim())
            .map((paragraph) => h('p', {}, [paragraph.trim()])),
        ]),
        h('div', { class: 'lf-dialog-actions' }, [close, accept]),
      ])
      this.listen(close, 'click', () => this.closeDialog())
      this.listen(accept, 'click', () => {
        if (this.lgpdInput) this.lgpdInput.checked = true
        this.setLgpdError(null)
        this.closeDialog()
      })
      this.root.append(dialog)
      this.dialog = dialog
    }
    if (typeof this.dialog.showModal === 'function') {
      try {
        this.dialog.showModal()
        return
      } catch {
        // cai no atributo open
      }
    }
    this.dialog.setAttribute('open', '')
  }

  private closeDialog(): void {
    if (!this.dialog) return
    if (typeof this.dialog.close === 'function') this.dialog.close()
    this.dialog.removeAttribute('open')
  }

  private bindForm(form: HTMLFormElement): void {
    this.listen(
      form,
      'submit',
      (event) => {
        event.preventDefault()
        void this.handleSubmit()
      },
      false
    )
    this.listen(form, 'focusin', () => this.markStarted())
    this.listen(form, 'input', (event) => this.onInput(event))
    this.listen(form, 'change', (event) => this.onChange(event))
    this.listen(form, 'focusout', (event) => this.onBlur(event))
  }

  private markStarted(): void {
    if (this.started) return
    this.started = true
    this.emit('start', {})
    if (this.canTrack()) pushDataLayer({ event: 'formStart', eventModel: { form_id: this.publicId } })
  }

  private canTrack(): boolean {
    return !this.preview && conversionsEnabled()
  }

  private stateFor(target: EventTarget | null): FieldState | undefined {
    if (!(target instanceof HTMLElement)) return undefined
    const name = target.getAttribute('name')
    return name ? this.fields.get(name) : undefined
  }

  private onInput(event: Event): void {
    this.markStarted()
    const state = this.stateFor(event.target)
    if (!state) return
    if (state.def.kind === 'phone' && state.control instanceof HTMLInputElement) this.applyPhoneMask(state.control)
    if (state.control.getAttribute('aria-invalid') === 'true') this.validateOne(state)
  }

  private onChange(event: Event): void {
    const state = this.stateFor(event.target)
    if (!state) return
    if (state.def.kind === 'state') this.loadCities(state.control.value)
    if (this.attempted || state.control.getAttribute('aria-invalid') === 'true') this.validateOne(state)
  }

  private onBlur(event: Event): void {
    const state = this.stateFor(event.target)
    if (state && this.attempted) this.validateOne(state)
  }

  private applyPhoneMask(input: HTMLInputElement): void {
    const raw = input.value
    const caret = input.selectionStart ?? raw.length
    const digitsBefore = onlyDigits(raw.slice(0, caret)).length
    const masked = maskPhone(raw)
    if (masked === raw) return
    input.value = masked
    if (caret >= raw.length) return
    let position = 0
    let seen = 0
    while (position < masked.length && seen < digitsBefore) {
      if (/\d/.test(masked[position] as string)) seen++
      position++
    }
    try {
      input.setSelectionRange(position, position)
    } catch {
      // alguns tipos de input não aceitam seleção
    }
  }

  private cityField(): FieldState | undefined {
    for (const state of this.fields.values()) {
      if (state.def.kind === 'city' && state.control instanceof HTMLSelectElement) return state
    }
    return undefined
  }

  private loadCities(uf: string, selected?: string): void {
    const city = this.cityField()
    if (!city || !this.api) return
    const select = city.control as HTMLSelectElement
    const request = ++this.cityRequest
    const placeholder = (text: string) => h('option', { value: '' }, [text])
    if (!uf) {
      select.replaceChildren(placeholder(UI_TEXT.cityWaiting))
      select.disabled = true
      return
    }
    select.replaceChildren(placeholder(UI_TEXT.cityLoading))
    select.disabled = true
    const key = `${this.options.apiBase}|${uf}`
    const pending = citiesCache.get(key) ?? this.api.getCities(uf)
    citiesCache.set(key, pending)
    pending
      .then((cities) => {
        if (this.destroyed || request !== this.cityRequest) return
        select.replaceChildren(
          placeholder(city.def.placeholder || UI_TEXT.cityPlaceholder),
          ...cities.map((name) => h('option', { value: name }, [name]))
        )
        select.disabled = false
        if (selected) select.value = selected
      })
      .catch(() => {
        citiesCache.delete(key)
        if (this.destroyed || request !== this.cityRequest) return
        select.replaceChildren(placeholder(UI_TEXT.cityWaiting))
      })
  }

  private applyValues(values: Record<string, string>): void {
    let stateValue: string | undefined
    for (const [key, raw] of Object.entries(values)) {
      const state = this.fields.get(key)
      if (!state || raw === undefined || raw === null) continue
      const value = String(raw)
      if (state.def.kind === 'city' && state.control instanceof HTMLSelectElement) continue
      state.control.value = state.def.kind === 'phone' ? maskPhone(value) : value
      if (state.def.kind === 'state') stateValue = value
    }
    const city = this.cityField()
    const cityValue = city ? values[city.def.key] : undefined
    if (stateValue !== undefined) this.loadCities(stateValue, cityValue)
  }

  private setFieldError(state: FieldState, message: string | null): void {
    if (!state.error) return
    state.error.textContent = message ?? ''
    if (message) {
      state.control.setAttribute('aria-invalid', 'true')
      state.control.setAttribute('aria-describedby', state.error.id)
      return
    }
    state.control.removeAttribute('aria-invalid')
    state.control.removeAttribute('aria-describedby')
  }

  private setLgpdError(message: string | null): void {
    if (!this.lgpdError || !this.lgpdInput) return
    this.lgpdError.textContent = message ?? ''
    if (message) {
      this.lgpdInput.setAttribute('aria-invalid', 'true')
      this.lgpdInput.setAttribute('aria-describedby', this.lgpdError.id)
      return
    }
    this.lgpdInput.removeAttribute('aria-invalid')
    this.lgpdInput.removeAttribute('aria-describedby')
  }

  private setFormError(message: string): void {
    if (this.formError) this.formError.textContent = message
  }

  private validateOne(state: FieldState): string | null {
    if (!state.error) return null
    const message = validateField(state.def, state.control.value, this.messages)
    this.setFieldError(state, message)
    return message
  }

  private validateAll(): { element: HTMLElement; field: string; message: string } | null {
    let first: { element: HTMLElement; field: string; message: string } | null = null
    this.fields.forEach((state, key) => {
      const message = this.validateOne(state)
      if (message && !first) first = { element: state.control, field: key, message }
    })
    const lgpd = this.settings.lgpd
    if (lgpd.enabled && lgpd.required && this.lgpdInput && !this.lgpdInput.checked) {
      this.setLgpdError(this.messages.lgpd)
      first = first ?? { element: this.lgpdInput, field: 'lgpd', message: this.messages.lgpd }
    }
    return first
  }

  private collectValues(): Record<string, string> {
    const values: Record<string, string> = {}
    this.fields.forEach((state, key) => {
      const raw = state.control.value.trim()
      if (!raw) return
      values[key] = state.def.kind === 'phone' ? normalizePhone(raw) : raw
    })
    return values
  }

  private setButton(state: ButtonState): void {
    this.buttonState = state
    const button = this.button
    if (!button) return
    button.setAttribute('data-state', state)
    button.textContent = this.settings.button[state]
    ;(Object.keys(BUTTON_CLASS) as ButtonState[]).forEach((name) => {
      const userKey = BUTTON_CLASS[name]
      const active = name === state
      button.classList.toggle(`lf-button--${name}`, active)
      const userClass = userKey ? this.options.classes?.[userKey] : undefined
      if (userClass) userClass.split(/\s+/).filter(Boolean).forEach((item) => button.classList.toggle(item, active))
    })
    if (state === 'idle') button.removeAttribute('aria-disabled')
    else button.setAttribute('aria-disabled', 'true')
    if (state === 'sending') button.setAttribute('aria-busy', 'true')
    else button.removeAttribute('aria-busy')
  }

  private async handleSubmit(): Promise<void> {
    const definition = this.definition
    if (!definition || !this.formEl || this.buttonState !== 'idle' || this.destroyed) return
    this.setFormError('')
    this.attempted = true
    const invalid = this.validateAll()
    if (invalid) {
      invalid.element.focus()
      this.emit('error', { code: 'validation', message: invalid.message, field: invalid.field })
      return
    }
    if (definition.type === 'update' && !this.token) {
      this.handleMissingSubmission()
      return
    }

    const values = this.collectValues()
    this.setButton('sending')
    this.emit('submit', { values: { ...values } })
    if (!this.preview) this.pageForm?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))

    const minMs = this.settings.protection.minFillSeconds * 1000
    const waited = now() - this.renderedAt
    if (!this.preview && waited < minMs) await sleep(minMs - waited + 50)
    if (this.destroyed) return

    if (containsBlockedWord(values.title ?? '', this.options.blockedWords ?? [])) {
      await sleep(FAKE_DELAY_MS)
      if (!this.destroyed) this.finish(null, false)
      return
    }

    const body: CreateSubmissionBody = {
      values,
      tracking: collectTracking(this.settings.tracking.utmParams),
      elapsedMs: Math.round(now() - this.renderedAt),
    }
    const { pipeId, stageId } = this.options.overrides ?? {}
    if (pipeId !== undefined || stageId !== undefined) body.overrides = { pipeId, stageId }
    if (this.options.landingId !== undefined) body.landingId = this.options.landingId
    if (this.settings.lgpd.enabled && this.lgpdInput) body.lgpdAccepted = this.lgpdInput.checked
    if (this.honeypot) body.hp = this.honeypot.value

    try {
      const result = await this.send(definition, body)
      if (this.destroyed) return
      if (definition.type === 'create' && !this.preview) {
        const kept: Record<string, string> = {}
        if (values.title) kept.title = values.title
        if (values.phone) kept.phone = values.phone
        saveSubmission(result.submissionToken, kept)
      }
      this.token = result.submissionToken
      this.trackSuccess(result.eventId, values)
      this.emit('success', { eventId: result.eventId, submissionToken: result.submissionToken })
      this.finish(result.submissionToken, true)
    } catch (error) {
      if (!this.destroyed) this.handleSubmitError(error)
    }
  }

  private async send(definition: FormDefinition, body: CreateSubmissionBody): Promise<{ submissionToken: string; eventId: string }> {
    if (this.preview || !this.api) {
      await sleep(PREVIEW_DELAY_MS)
      return { submissionToken: 'preview', eventId: 'preview' }
    }
    if (definition.type === 'create') return this.api.createSubmission(this.publicId, body)
    const { overrides: _overrides, landingId: _landingId, ...updateBody } = body
    const token = this.token as string
    const result = await this.api.updateSubmission(this.publicId, token, updateBody)
    return { submissionToken: token, eventId: result.eventId }
  }

  private trackSuccess(eventId: string, values: Record<string, string>): void {
    if (!this.canTrack()) return
    trackPixel(this.settings.tracking.metaEventName, eventId)
    const emailField = this.definition?.fields.find((field) => field.inputType === 'email')
    pushDataLayer({
      event: 'formSubmit',
      eventModel: {
        form_id: this.publicId,
        user_name: values.title ?? '',
        user_phone: values.phone ?? '',
        user_email: (emailField && values[emailField.key]) || '',
      },
    })
  }

  private handleSubmitError(error: unknown): void {
    const detail: FormError =
      error instanceof ApiError ? { ...error.detail } : { code: 'unknown', message: UI_TEXT.genericError }
    if (detail.code === 'invalid_token') {
      removeKey(SUBMISSION_KEY)
      this.token = null
    }
    this.setButton('idle')
    let shownInline = false
    const fieldState = detail.field ? this.fields.get(detail.field) : undefined
    if (fieldState?.error) {
      this.setFieldError(fieldState, detail.message)
      fieldState.control.focus()
      shownInline = true
    } else if (detail.field === 'lgpd' && this.lgpdInput) {
      this.setLgpdError(detail.message)
      this.lgpdInput.focus()
      shownInline = true
    }
    const listeners = this.emit('error', detail)
    if (listeners === 0 && !shownInline) this.setFormError(detail.message)
  }

  private finish(token: string | null, real: boolean): void {
    this.setButton('sent')
    const { successMessage, redirectUrl } = this.settings.afterSubmit
    if (successMessage.enabled) this.showSuccess()
    if (!redirectUrl) return
    const go = () => {
      this.setButton('redirecting')
      this.redirect(redirectUrl, real ? token : null)
    }
    if (successMessage.enabled) this.later(go, Math.max(0, successMessage.durationMs))
    else go()
  }

  private showSuccess(): void {
    if (!this.root || !this.formEl) return
    const { title, text } = this.settings.afterSubmit.successMessage
    const height = this.formEl.offsetHeight
    const success = h('div', { class: this.cls('lf-success', 'success'), part: 'success', role: 'status', tabindex: -1 }, [
      title ? h('p', { class: 'lf-success-title' }, [title]) : null,
      text ? h('p', { class: 'lf-success-text' }, [text]) : null,
    ])
    if (height > 0) success.style.minHeight = `${height}px`
    this.formEl.hidden = true
    this.successEl?.remove()
    this.successEl = success
    this.root.append(success)
    this.root.setAttribute('data-lf-state', 'success')
    try {
      success.focus({ preventScroll: true })
    } catch {
      // foco é cortesia de acessibilidade
    }
  }

  private redirect(url: string, token: string | null): void {
    if (!isSafeNavigationUrl(url)) return
    const finalUrl = appendTokenIfCrossHost(url, token)
    let prevented = false
    this.emit('redirect', { url: finalUrl, preventDefault: () => (prevented = true) })
    if (prevented || this.preview) return
    window.location.assign(finalUrl)
  }
}
